/**
 * HOOK: useBedActions
 * Action d'émission à froid d'un lien de paiement BED (TICKET-BED-ENTRY).
 *
 * Modèle : usePaymentActions (mapRpcError(message) -> message FR). Délègue à la
 * RPC `create_bed_payment_case` (migration 00130) qui crée atomiquement, pour un
 * équipage/contact ayant déjà payé son BPI :
 *   - un payment_case scope='bed' status='draft' (montant saisi par l'admin) ;
 *   - un bed_token (TTL 30 jours par défaut) ;
 *   - un timeline_event 'bed_link_generated'.
 *
 * Le lien public reste /paiement/bed?token= (slug FR universel, JAMAIS traduit
 * — emails BED + iframe CRM + IPN reposent dessus).
 */

import { useCallback } from 'react';
import { useSupabase } from './useSupabase';
import { resolvePaymentBaseUrl } from '../lib/payment/product-payment-sites';

// Construit le lien public du tunnel BED pour un token donné. Partagé entre
// BedLinkDialog (lien fraîchement généré) et PaymentSection (ré-affichage du
// lien actif sur un dossier BED existant).
//
// Lot 2 BED (GAP-2) : la base du tunnel est résolue PAR PRODUIT depuis le CODE
// PRODUIT réel du dossier (`products.code`, ex `4lt`/`rdt`) — cf.
// resolvePaymentBaseUrl. Défaut = tunnel partagé (4L Trophy). Le jour où Rose
// Trip (code `rdt`) a son propre tunnel, c'est une entrée de config
// (product-payment-sites.ts), pas une modif ici.
// ⚠️ Review Victor #3 : on ne route PLUS sur le préfixe de l'order_id
// (`split('-')[0]`) — le format Sandra réel est `9274L1455` sans tiret, le
// préfixe extrait n'était jamais un code produit. `productCode` vient du
// serveur (RPC.product_code / paymentCase.product_code). Optionnel pour compat :
// absent/inconnu → tunnel partagé (fail-open assumé).
// Le slug /paiement/bed n'est jamais localisé (décision MEMORY
// project_4lt_paiement_non_localise).
export function buildBedPaymentLink(token: string, productCode?: string | null): string {
  const baseUrl = resolvePaymentBaseUrl(productCode ?? undefined);
  // Slug /paiement/bed JAMAIS traduit (slug FR universel).
  return `${baseUrl}/paiement/bed?token=${token}`;
}

interface GenerateBedLinkParams {
  // Un seul des deux porteurs est requis (la RPC privilégie le crew si les deux
  // sont fournis). Aligne avec le CHECK payment_cases_has_owner (OR, pas XOR).
  crewId?: string;
  contactId?: string;
  // Montant BED en centimes. Depuis 00142, la RPC RECALCULE le solde
  // serveur-side (prix_total produit − Σ acompte BPI payé) et IGNORE cette
  // valeur : elle n'est plus qu'informative (le solde pré-affiché). Conservée
  // pour la compat de signature (régénération repasse le montant figé du case).
  amountCents: number;
  // Payeur optionnel pré-renseigné sur le dossier BED.
  payerContactId?: string;
  // Dossier BPI payé parent (paidBpiCaseId de PaymentSection). Lève
  // l'ambiguïté produit côté RPC quand un contact a payé plusieurs BPI.
  parentBpiCaseId?: string;
}

export interface GenerateBedLinkResult {
  success: boolean;
  // payment_case_id du dossier BED créé/réutilisé. Source de l'idempotencyKey
  // Resend ({payment_case_id}-bedlink) pour l'envoi de l'email.
  paymentCaseId?: string;
  bedToken?: string;
  bedLink?: string;
  orderId?: string;
  // Montant effectif du dossier (centimes) : peut différer du montant demandé
  // si un BED non-terminal a été réutilisé sans mise à jour (montant figé).
  amountCents?: number;
  // true si un BED existant a été réutilisé (pas de nouveau case créé).
  reused?: boolean;
  // true si le montant/payeur demandés ont été appliqués au case réutilisé
  // (draft sans encaissement uniquement).
  amountUpdated?: boolean;
  // Message d'erreur traductible remonté à l'UI quand success=false.
  error?: string;
}

// Forme du JSONB retourné par create_bed_payment_case (migration 00144).
interface CreateBedPaymentCaseRow {
  payment_case_id: string;
  bed_token: string;
  order_id: string;
  // Code produit réel du dossier (products.code) : route le tunnel de paiement
  // PAR PRODUIT (review Victor #3). Peut être null si le produit n'a pas de code.
  product_code: string | null;
  expires_at: string;
  amount_cents: number;
  reused: boolean;
  amount_updated: boolean;
}

// Mapping code erreur PG (RAISE EXCEPTION dans la RPC 00144) -> message UI FR.
function mapRpcError(message: string): GenerateBedLinkResult {
  // Garde-fou prix (review Victor #2) : prix produit non confirmé.
  if (message.includes('bed_price_unverified')) {
    return {
      success: false,
      error:
        "Le prix total de ce produit n'a pas encore été confirmé : impossible d'émettre un lien BED tant que le montant n'est pas validé.",
    };
  }
  if (message.includes('bed_solde_not_positive')) {
    return {
      success: false,
      error:
        'Le solde BED calculé est nul ou négatif (produit déjà soldé ou prix mal configuré) : aucun lien à émettre.',
    };
  }
  if (message.includes('bed_amount_must_be_positive')) {
    return {
      success: false,
      error: 'Le montant du solde BED doit être strictement positif.',
    };
  }
  if (message.includes('bed_expiry_must_be_positive')) {
    return {
      success: false,
      error: 'La durée de validité du lien doit être strictement positive.',
    };
  }
  if (message.includes('no_paid_bpi_parent')) {
    return {
      success: false,
      error:
        "Aucun BPI payé n'a été trouvé pour ce dossier : le solde BED ne peut être émis qu'après le paiement du BPI.",
    };
  }
  if (message.includes('bed_case_already_terminal')) {
    return {
      success: false,
      error:
        'Un dossier BED existe déjà pour cet équipage et il est dans un statut terminal (payé, expiré ou échoué). Veuillez rafraîchir la page.',
    };
  }
  if (message.includes('bed_owner_required')) {
    return {
      success: false,
      error: 'Aucun équipage ni contact fourni pour ce lien BED.',
    };
  }
  if (message.includes('crew_not_found')) {
    return { success: false, error: 'Équipage introuvable.' };
  }
  if (message.includes('invalid_parent_bpi_case')) {
    return {
      success: false,
      error:
        'Le dossier BPI parent fourni est invalide (il doit être un BPI payé appartenant à ce porteur). Veuillez rafraîchir la page.',
    };
  }
  return { success: false };
}

// Breakdown du solde BED pré-calculé par le CRM (RPC get_bed_solde, 00142).
// Montants en centimes. solde = prixTotal − acompte − réduction (00164).
export interface BedSoldeBreakdown {
  prixTotalCents: number;
  acompteCents: number;
  reductionCents: number;
  soldeCents: number;
}

export interface FetchBedSoldeResult {
  success: boolean;
  solde?: BedSoldeBreakdown;
  error?: string;
}

// Forme du JSONB retourné par get_bed_solde (migration 00142, clé
// reduction_cents ajoutée par 00164 — optionnelle le temps du déploiement).
interface GetBedSoldeRow {
  product_id: string;
  prix_total_cents: number;
  acompte_cents: number;
  reduction_cents?: number;
  solde_cents: number;
}

// Mapping des erreurs PG de get_bed_solde -> message UI FR.
function mapSoldeError(message: string): FetchBedSoldeResult {
  if (message.includes('invalid_parent_bpi_case')) {
    return {
      success: false,
      error: "Aucun BPI payé rattaché à ce dossier : le solde BED n'est pas calculable.",
    };
  }
  // Garde-fou prix (review Victor #2) : prix produit non confirmé -> pas de preview.
  if (message.includes('bed_price_unverified')) {
    return {
      success: false,
      error:
        "Le prix total de ce produit n'a pas encore été confirmé : le solde BED ne peut pas être calculé tant que le montant n'est pas validé.",
    };
  }
  if (message.includes('product_price_missing')) {
    return {
      success: false,
      error: "Le prix total du produit n'est pas renseigné : impossible de calculer le solde BED.",
    };
  }
  return { success: false, error: 'Erreur lors du calcul du solde BED.' };
}

export function useBedActions() {
  const supabase = useSupabase();

  // Solde BED pré-calculé serveur-side (prix_total produit − Σ acompte BPI
  // payé). Appelé par BedLinkDialog à l'ouverture pour AFFICHER le solde en
  // lecture seule (le CDC interdit tout override client — GAP-1).
  const fetchBedSolde = useCallback(
    async (parentBpiCaseId: string): Promise<FetchBedSoldeResult> => {
      try {
        const { data, error } = await supabase.rpc('get_bed_solde', {
          p_parent_bpi_case_id: parentBpiCaseId,
        });
        if (error) {
          console.error('[useBedActions] fetchBedSolde RPC error:', error);
          return mapSoldeError(error.message ?? '');
        }
        const row = data as GetBedSoldeRow | null;
        if (!row || typeof row.solde_cents !== 'number') {
          console.error('[useBedActions] fetchBedSolde unexpected payload:', data);
          return { success: false, error: 'Erreur lors du calcul du solde BED.' };
        }
        return {
          success: true,
          solde: {
            prixTotalCents: row.prix_total_cents,
            acompteCents: row.acompte_cents,
            reductionCents: row.reduction_cents ?? 0,
            soldeCents: row.solde_cents,
          },
        };
      } catch (err) {
        console.error('[useBedActions] fetchBedSolde error:', err);
        return { success: false, error: 'Erreur lors du calcul du solde BED.' };
      }
    },
    [supabase]
  );

  const generateBedLink = useCallback(
    async ({
      crewId,
      contactId,
      amountCents,
      payerContactId,
      parentBpiCaseId,
    }: GenerateBedLinkParams): Promise<GenerateBedLinkResult> => {
      try {
        // RPC atomique (migration 00130) : INSERT payment_case BED + bed_token
        // + timeline_event dans une seule transaction. Le montant n'est pas
        // recalculé côté serveur, il est saisi ici (auto-calcul différé BED-10).
        const { data, error } = await supabase.rpc('create_bed_payment_case', {
          p_crew_id: crewId ?? null,
          p_contact_id: contactId ?? null,
          p_amount_cents: amountCents,
          p_payer_contact_id: payerContactId ?? null,
          p_parent_bpi_case_id: parentBpiCaseId ?? null,
        });

        if (error) {
          console.error('[useBedActions] generateBedLink RPC error:', error);
          return mapRpcError(error.message ?? '');
        }

        const row = data as CreateBedPaymentCaseRow | null;
        if (!row?.bed_token) {
          console.error('[useBedActions] generateBedLink unexpected payload:', data);
          return { success: false };
        }

        return {
          success: true,
          paymentCaseId: row.payment_case_id,
          bedToken: row.bed_token,
          bedLink: buildBedPaymentLink(row.bed_token, row.product_code),
          orderId: row.order_id,
          amountCents: row.amount_cents,
          reused: row.reused,
          amountUpdated: row.amount_updated,
        };
      } catch (err) {
        console.error('[useBedActions] generateBedLink error:', err);
        return { success: false };
      }
    },
    [supabase]
  );

  return { generateBedLink, fetchBedSolde };
}
